import React, { FC, useContext } from 'react';
import { ArrowContainer } from 'react-tiny-popover';
import { HiEye } from 'react-icons/hi';
import { usePreviewToggle } from '@/features/editor/widgets/shared/usePreviewDetails';
import {
  Experience,
  TargetingDeviceType,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { Chip } from '@/features/details/shared';
import { DeviceType } from '@/utils/definitions';
import { AccountContext } from '@/features/account-context';
import { ResponsivePopover } from '@/components/responsive-popover';
import {
  outsideButNotReactSelect,
  PreviewPopoverContentNew,
} from '@/features/editor/widgets/shared/preview-popover-new';

type Props = { experience: Experience };

export const PreviewChip: FC<Props> = ({ experience }) => {
  const {
    account: {
      store: { domain },
    },
  } = useContext(AccountContext);
  const deviceType =
    experience.lastDevicePreview ||
    experience.quickPreviewDevice ||
    experience?.targeting?.device === TargetingDeviceType.DESKTOP
      ? DeviceType.Desktop
      : DeviceType.Mobile;

  const { previewPopoverOpen, togglePopover } = usePreviewToggle();
  const { generatePreviewLink, sharePreviewLink } = useExperienceApi(
    deviceType,
    experience,
  );
  const onPreview = async (quickPreviewUrl: string, variantId?: string) => {
    const finalPreviewUrl =
      quickPreviewUrl.includes(`sdk.loomi`) ||
      quickPreviewUrl.includes(`shopify_checkout_extensibility`)
        ? `${domain}`
        : quickPreviewUrl;
    const { link } = await generatePreviewLink(
      experience.id,
      experience.variants[0].id,
      finalPreviewUrl,
      variantId || experience.activeVariantId,
    );
    try {
      const l = new URL(link);
      l.searchParams.delete(`preview_theme_id`);
      return l.toString();
    } catch (e) {
      return link;
    }
  };

  const onSharePreview = (
    email: string,
    sharedBy: string,
    quickPreviewUrl: string,
  ) =>
    sharePreviewLink({
      email,
      sharedBy,
      options: {
        experienceId: experience.id,
        variantId: experience.variants?.[0]?.id,
        quickPreviewUrl,
      },
    });

  if (!deviceType) {
    return null;
  }

  return (
    <ResponsivePopover
      containerStyle={{ zIndex: `999` }}
      isOpen={previewPopoverOpen}
      positions={[`bottom`]}
      onClickOutside={outsideButNotReactSelect(
        togglePopover,
        `exp-preview-content`,
      )}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={20}
          arrowStyle={{ opacity: 1 }}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
        >
          <PreviewPopoverContentNew
            onPreview={onPreview}
            onSharePreview={onSharePreview}
            variants={experience.variants}
            quickPreviewUrl={experience.quickPreviewUrl}
            togglePopover={togglePopover}
          />
        </ArrowContainer>
      )}
    >
      <Chip onClick={togglePopover}>
        <HiEye style={{ marginRight: `0.5rem` }} /> Preview
      </Chip>
    </ResponsivePopover>
  );
};
